/**
 * Plain-data 3-vector math. Depends on: nothing. Pure; no mutation.
 * Convention: right-handed, +Y up (ADR-003 §2a).
 */

export interface Vec3Data {
  readonly x: number;
  readonly y: number;
  readonly z: number;
}

export const VEC3_ZERO: Vec3Data = Object.freeze({ x: 0, y: 0, z: 0 });

export function vec3(x: number, y: number, z: number): Vec3Data {
  return { x, y, z };
}

export function vec3Add(a: Vec3Data, b: Vec3Data): Vec3Data {
  return { x: a.x + b.x, y: a.y + b.y, z: a.z + b.z };
}

export function vec3Sub(a: Vec3Data, b: Vec3Data): Vec3Data {
  return { x: a.x - b.x, y: a.y - b.y, z: a.z - b.z };
}

export function vec3Scale(v: Vec3Data, s: number): Vec3Data {
  return { x: v.x * s, y: v.y * s, z: v.z * s };
}

export function vec3Length(v: Vec3Data): number {
  return Math.hypot(v.x, v.y, v.z);
}

export function vec3Dot(a: Vec3Data, b: Vec3Data): number {
  return a.x * b.x + a.y * b.y + a.z * b.z;
}

export function vec3Cross(a: Vec3Data, b: Vec3Data): Vec3Data {
  return {
    x: a.y * b.z - a.z * b.y,
    y: a.z * b.x - a.x * b.z,
    z: a.x * b.y - a.y * b.x,
  };
}

export function vec3Normalize(v: Vec3Data): Vec3Data {
  const len = vec3Length(v);
  if (len === 0 || !Number.isFinite(len)) {
    // A zero/non-finite vector has no direction — same fail-loud contract as
    // quatNormalize (ADR-001 §7).
    throw new RangeError('vec3Normalize: zero or non-finite vector has no direction');
  }
  return { x: v.x / len, y: v.y / len, z: v.z / len };
}
